import _ from 'lodash';
import { fromJS, List, Map, OrderedMap } from 'immutable';
import BookmarkConstants from '../constants/BookmarkConstants';
import CollectionConstants from '../constants/CollectionConstants';
import InventoryRecordConstants from '../constants/InventoryRecordConstants';
import StoreConstants from '../constants/StoreConstants';
import PaginatedStore, {RequestStates} from './PaginatedStore';
import InventoryRecord from '../models/InventoryRecord';
import SimilarProductsCategory from '../models/SimilarProductsCategory';

class InventoryRecordStore extends PaginatedStore {
  static storeName = 'InventoryRecordStore';
  static handlers = {
    [InventoryRecordConstants.FETCH_INVENTORY_RECORD_STATS_SUCCESS]    : 'fetchStatsSuccess',
    [InventoryRecordConstants.FETCH_INVENTORY_RECORD_STATS_FAILURE]    : 'fetchStatsFailure',
    [InventoryRecordConstants.FETCH_INVENTORY_RECORD_START]            : 'fetchEntityStart',
    [InventoryRecordConstants.FETCH_INVENTORY_RECORD_SUCCESS]          : 'fetchEntitySuccess',
    [InventoryRecordConstants.FETCH_INVENTORY_RECORD_FAILURE]          : 'fetchEntityFailure',
    [InventoryRecordConstants.FETCH_SIMILAR_INVENTORY_RECORDS_START]   : 'fetchSimilarStart',
    [InventoryRecordConstants.FETCH_SIMILAR_INVENTORY_RECORDS_SUCCESS] : 'fetchSimilarSuccess',
    [InventoryRecordConstants.FETCH_SIMILAR_INVENTORY_RECORDS_FAILURE] : 'fetchSimilarFailure',
    [InventoryRecordConstants.LIST_INVENTORY_RECORDS_START]            : 'listEntitiesStart',
    [InventoryRecordConstants.LIST_INVENTORY_RECORDS_SUCCESS]          : 'listEntitiesSuccess',
    [InventoryRecordConstants.LIST_INVENTORY_RECORDS_FAILURE]          : 'listEntitiesFailure',
    [InventoryRecordConstants.SET_CURRENT_INVENTORY_RECORD]            : 'setCurrentEntity',
    [BookmarkConstants.CREATE_BOOKMARK_SUCCESS]                        : 'createBookmarkSuccess',
    [BookmarkConstants.DELETE_BOOKMARK_SUCCESS]                        : 'deleteBookmarkSuccess',
    [CollectionConstants.FETCH_COLLECTION_SUCCESS]                     : 'fetchCollectionSuccess',
    [StoreConstants.FETCH_STORE_FEED_SUCCESS]                          : 'fetchStoreFeedSuccess',
  };
  static defaultPagination = { offset: 0, limit: 15 };
  static defaultSorter = { key: 'rating', direction: 'desc' };
  static entityModel = InventoryRecord;

  constructor(dispatcher) {
    super(dispatcher);

    this.stats = null;
    this.statsByStoreId = new Map();
    this.similarProductCategoriesRequestStatusMap = new Map();
    this.similarProductCategoriesByInventoryRecordId = new Map();
    this.suggestedFilters = [];
    this.currentRouteName = null;
  }

  fetchCollectionSuccess(data) {
    // Cache inventory records from collections here for smooth transition to InventoryRecordDetail from Collection
    data.collection.items.forEach((collectionItem) => {
      this.entities = this.entities.set(collectionItem.inventory_record.id, collectionItem.inventory_record);
    });
    this.emitChange();
  }

  fetchStoreFeedSuccess(data) {
    // Cache inventory records from store feed for smooth transitions
    data.storeFeed.items.forEach((item) => {
      if(item.type === 'product_group') {
        item.object.inventory_records.forEach((inventoryRecord) => {
          this.entities = this.entities.set(inventoryRecord.id, inventoryRecord);
        });
      }
    });
    this.emitChange();
  }

  createBookmarkSuccess(data) {
    let id = data.inventoryRecord.get('id');
    if(this.orderedEntities.get(id)) {
      this.orderedEntities = this.orderedEntities.set(id, data.inventoryRecord);
    }
    this.entities = this.entities.set(id, data.inventoryRecord);
    this.emitChange();
  }

  deleteBookmarkSuccess(data) {
    let id = data.inventoryRecord.id;
    let newRecord = this.entities.get(id);
    if(newRecord) {
      newRecord = newRecord.set('customer_context', data.inventoryRecord.customer_context);
    } else {
      newRecord = data.inventoryRecord;
    }
    if(this.orderedEntities.get(id)) {
      this.orderedEntities = this.orderedEntities.set(id, newRecord);
    }
    this.entities = this.entities.set(id, newRecord);
    this.emitChange();
  }

  fetchStatsSuccess(data) {
    if(data.storeId) {
      this.statsByStoreId = this.statsByStoreId.set(data.storeId, data.stats);
    } else {
      this.stats = data.stats;
    }
    this.emitChange();
  }

  fetchStatsFailure(error) {
    console.log('Error fetching stats: ', error);
    this.logError(error);
  }

  listEntitiesStart(data) {
    // if(!_.isEqual(data.sorter, this.currentSorter) || !_.isEqual(data.filters, this.currentFilters)) {
    //   this.suggestedFilters = [];
    // }
    super.listEntitiesStart(data);
  }

  listEntitiesSuccess(data) {
    if(!_.isEqual(data.sorter, this.currentSorter) || !_.isEqual(data.filters, this.currentFilters)) {
      return;
    }

    this.suggestedFilters = data.suggestedFilters;
    super.listEntitiesSuccess(data);
  }

  fetchSimilarStart(entityId) {
    this.similarProductCategoriesRequestStatusMap = this.similarProductCategoriesRequestStatusMap.set(entityId, RequestStates.Loading);
    this.emitChange();
  }

  fetchSimilarSuccess(data) {
    this.similarProductCategoriesByInventoryRecordId = this.similarProductCategoriesByInventoryRecordId.set(data.entityId, data.categories);
    data.categories.forEach((similarProductCategory) => {
      similarProductCategory.inventory_records.forEach((inventoryRecord) => {
        this.entities = this.entities.set(inventoryRecord.id, inventoryRecord);
      });
    });
    this.similarProductCategoriesRequestStatusMap = this.similarProductCategoriesRequestStatusMap.set(data.entityId, RequestStates.Done);
    this.emitChange();
  }

  fetchSimilarFailure(data) {
    this.logError(data.error);
    this.similarProductCategoriesRequestStatusMap = this.similarProductCategoriesRequestStatusMap.set(data.entityId, RequestStates.Error);
    this.emitChange();
  }

  getStats(storeId) {
    if(storeId) {
      return this.statsByStoreId.get(storeId);
    }
    return this.stats;
  }

  getSimilarProductsCategoriesByInventoryRecordId(inventoryRecordId) {
    return this.similarProductCategoriesByInventoryRecordId.get(inventoryRecordId);
  }

  getSimilarProductsCategoriesRequestStatusByInventoryRecordId(inventoryRecordId) {
    return this.similarProductCategoriesRequestStatusMap.get(inventoryRecordId);
  }

  getCurrentSearchState() {
    return {
      inventoryRecords: this.orderedEntities,
      suggestedFilters: this.suggestedFilters,
      filters: this.currentFilters,
      sorter: this.currentSorter,
      pagination: this.currentPagination,
      loading: this.loading
    };
  }

  dehydrate() {
    return _.extend(super.dehydrate(), {
      stats: this.stats,
      statsByStoreId: this.statsByStoreId.toJS(),
      similarProductCategoriesByInventoryRecordId: this.similarProductCategoriesByInventoryRecordId.toJS(),
      suggestedFilters: this.suggestedFilters,
      currentRouteName: this.currentRouteName
    });
  }

  rehydrate(state) {
    super.rehydrate(state);
    this.stats = state.stats;
    this.statsByStoreId = new Map(state.statsByStoreId);
    this.similarProductCategoriesByInventoryRecordId = _.reduce(state.similarProductCategoriesByInventoryRecordId, (map, categoriesList, inventoryRecordId) => {
      let list = _.reduce(categoriesList, (list, similarProductCategory) => {
        let inventoryRecords = _.map(similarProductCategory.inventory_records, (inventoryRecord) => {
          let model = this.entities.get(inventoryRecord.id);
          if(!model) {
            model = new InventoryRecord(inventoryRecord);
          }
          return model;
        });
        return list.push(new SimilarProductsCategory(_.extend({}, similarProductCategory, {inventory_records: inventoryRecords})));
      }, new List());
      return map.set(inventoryRecordId, list);
    }, new Map());
    this.suggestedFilters = state.suggestedFilters;
    this.currentRouteName = state.currentRouteName;
  }
}

export default InventoryRecordStore;



// WEBPACK FOOTER //
// banquet/stores/InventoryRecordStore.js